// AmadeusProvider — thin class that wires the split modules together. The
// heavy lifting lives in:
//
//   ./normalize      validate + normalize + shape builders
//   ./upstream       service_handler wrappers (search, price, seatmaps, …)
//   ./seats          apply_seats_to_raw + fetch_seats_applied
//
// Dictionary cache helpers live in ../base/helpers — every provider
// returning Amadeus-shape `{ locations, carriers }` shares one impl.
//
// The class's only job is to satisfy the Provider contract by delegating
// each method to the right module, carry the capability manifest, and own
// instance state (_carriers, _airports, _extra_bags_catalog).

import Provider from '../base/provider';
import { validate, normalize } from './normalize';
import {
  search,
  fetch_search_suggestion,
  get_offer_details,
  fetch_seatmap,
  fetch_extra_bags,
  automated_checkin_offer,
  book,
  get_confirmation,
} from './upstream';
import { fetch_seats_applied, seats_are_mandatory } from './seats';
import { fetch_upsell, get_branded_fare, choose_upsell } from './upsell';
import {
  add_charged_bags,
  add_extra_bags,
  has_chargeable_bags,
  has_included_bags,
  get_bags,
} from './bags';
import {
  book_held,
  retrieve,
  fetch_ancillaries,
  book_ancillaries,
} from './ancillaries';

export default class AmadeusProvider extends Provider {
  static DEFAULT_CAPABILITIES = {
    seatSelection: true,
    extraBags: true,
    flexibleBags: true,
    shopSeats: true,
    shopBags: true,
    brandedFareUpsell: true,
    searchSuggestions: true,
    searchTriggerFilters: true,
    // Bags and seats gathered on /ancillaries, committed with one reprice
    // (see ./ancillaries). detail.vue reads it to hide its inline bags
    // panels; main.js overrides it from
    // guiSettings.customizations.ancillariesAsStep.
    ancillariesAsStep: true,
  };

  static DEFAULT_ENABLED = true;

  constructor({ providerId = 'amadeus', capabilities = {}, enabled } = {}) {
    super({ providerId, capabilities, enabled });
    this.version = 'amadeus@v2';
  }

  validate(raw) {
    return validate(this, raw);
  }

  normalize(raw) {
    return normalize(this, raw);
  }

  search(payload) {
    return search(this, payload);
  }

  fetch_search_suggestion(payload) {
    return fetch_search_suggestion(this, payload);
  }

  get_offer_details(flight_offer) {
    return get_offer_details(this, flight_offer);
  }

  fetch_seatmap(flight_offer, options) {
    return fetch_seatmap(this, flight_offer, options);
  }

  fetch_upsell(flight_offer) {
    return fetch_upsell(this, flight_offer);
  }

  fetch_extra_bags(flight_offer) {
    return fetch_extra_bags(this, flight_offer);
  }

  fetch_seats_applied(flight_offer, selections) {
    return fetch_seats_applied(this, flight_offer, selections);
  }

  seats_are_mandatory(flight_offer) {
    return seats_are_mandatory(flight_offer);
  }

  // ─── Fare choice (branded-fare upsell) ────────────────────────────────
  // All of these are pure on the normalized FlightOffer shape — the
  // Amadeus module exposes them so future providers can override per-
  // shape without touching the UI.

  get_branded_fare(flight_offer) {
    return get_branded_fare(flight_offer);
  }

  fetch_upsell_choice(flight_offer) {
    return choose_upsell(this, flight_offer);
  }

  // ─── Extra bags + flexible bags ───────────────────────────────────────

  add_charged_bags(flight_offer, flexible_bags) {
    return add_charged_bags(flight_offer, flexible_bags);
  }

  do_add_extra_bags(flight_offer, flexible_bags) {
    return add_extra_bags(this, flight_offer, flexible_bags);
  }

  has_chargeable_bags(flight_offer, traveler_id) {
    return has_chargeable_bags(flight_offer, traveler_id);
  }

  has_included_bags(flight_offer, traveler_id) {
    return has_included_bags(flight_offer, traveler_id);
  }

  get_bags(flight_offer, itinerary_index) {
    return get_bags(flight_offer, itinerary_index);
  }

  get_automated_checkin_offer(flight_offer, citizenship) {
    return automated_checkin_offer(this, flight_offer, citizenship);
  }

  // ─── Ancillaries-as-step (./ancillaries) ──────────────────────────────
  // Each hook defers to the base pass-through when the capability is off:
  // callers dispatch unconditionally, and a switch that redirects the UI
  // while the provider still reprices and holds is not a kill switch.

  book_held(flight_offer, traveler_details, contact_details) {
    if (!this.has_capability('ancillariesAsStep')) {
      return super.book_held(flight_offer, traveler_details, contact_details);
    }
    return book_held(this, flight_offer, traveler_details, contact_details);
  }

  retrieve(flight_offer, booking_id) {
    if (!this.has_capability('ancillariesAsStep')) {
      return super.retrieve(booking_id);
    }
    return retrieve(this, flight_offer, booking_id);
  }

  fetch_ancillaries(flight_offer, booking_id) {
    if (!this.has_capability('ancillariesAsStep')) {
      return super.fetch_ancillaries(booking_id);
    }
    return fetch_ancillaries(this, flight_offer, booking_id);
  }

  book_ancillaries(flight_offer, booking_id, diff) {
    if (!this.has_capability('ancillariesAsStep')) {
      return super.book_ancillaries(flight_offer, booking_id, diff);
    }
    return book_ancillaries(this, flight_offer, booking_id, diff);
  }

  book(flight_offer, request) {
    return book(this, flight_offer, request);
  }

  get_confirmation(body) {
    return get_confirmation(this, body);
  }
}
